import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'

export const useCartStore = defineStore('cart', () => {
  // State
  const items = ref([])
  const sessionId = ref(null)
  const loading = ref(false)
  const error = ref(null)
  
  // Getters
  const itemCount = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })
  
  const subtotal = computed(() => {
    return items.value.reduce((total, item) => total + (item.priceAtTime * item.quantity), 0)
  })
  
  const hasItems = computed(() => items.value.length > 0)
  
  // Helper to get or create session
  async function ensureSession() {
    if (!sessionId.value) {
      // Check localStorage first
      const savedSessionId = localStorage.getItem('cartSessionId')
      if (savedSessionId) {
        sessionId.value = savedSessionId
        // Try to load the cart
        await loadCart()
      } else {
        // Create new cart session
        await createCart()
      }
    }
    return sessionId.value
  }
  
  // Actions
  async function createCart() {
    try {
      loading.value = true
      error.value = null
      const response = await api.shop.cart.create()
      sessionId.value = response.data.data.sessionId
      localStorage.setItem('cartSessionId', sessionId.value)
      items.value = []
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to create cart'
      console.error('Cart creation error:', err)
    } finally {
      loading.value = false
    }
  }
  
  async function loadCart(showLoading = true) {
    if (!sessionId.value) return
    
    try {
      if (showLoading) {
        loading.value = true
      }
      error.value = null
      const response = await api.shop.cart.get(sessionId.value)
      const cartData = response.data.data
      
      // Map backend items to frontend format
      items.value = cartData.items.map(item => ({
        id: item.id,
        productId: item.product.id,
        name: item.product.name,
        price: item.product.price,
        priceAtTime: item.priceAtTime,
        quantity: item.quantity,
        imageUrl: item.product.imageUrl,
        sku: item.product.sku,
        lineTotal: item.lineTotal
      }))
    } catch (err) {
      if (err.response?.status === 404) {
        // Cart not found or expired, create new one
        localStorage.removeItem('cartSessionId')
        sessionId.value = null
        items.value = []
      } else {
        error.value = err.response?.data?.error || 'Failed to load cart'
        console.error('Cart load error:', err)
      }
    } finally {
      if (showLoading) {
        loading.value = false
      }
    }
  }
  
  async function addItem(product, quantity = 1) {
    try {
      await ensureSession()
      loading.value = true
      error.value = null
      
      const response = await api.shop.cart.addItem(sessionId.value, product.id, quantity)
      
      // Reload cart to get updated state
      await loadCart()
      
      return response.data.data
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to add item to cart'
      console.error('Add to cart error:', err)
      throw err
    } finally {
      loading.value = false
    }
  }
  
  async function removeItem(productId) {
    try {
      await ensureSession()
      loading.value = true
      error.value = null
      
      await api.shop.cart.removeItem(sessionId.value, productId)
      
      // Reload cart to get updated state
      await loadCart()
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to remove item'
      console.error('Remove item error:', err)
    } finally {
      loading.value = false
    }
  }
  
  async function updateQuantity(productId, quantity) {
    try {
      await ensureSession()
      // Don't show loading for quick quantity updates
      error.value = null
      
      await api.shop.cart.updateItem(sessionId.value, productId, quantity)
      
      // Reload cart to get updated state without showing loading spinner
      await loadCart(false)
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to update quantity'
      console.error('Update quantity error:', err)
    }
  }
  
  async function clearCart() {
    try {
      if (!sessionId.value) return
      
      loading.value = true
      error.value = null
      
      await api.shop.cart.clear(sessionId.value)
      items.value = []
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to clear cart'
      console.error('Clear cart error:', err)
    } finally {
      loading.value = false
    }
  }
  
  // Initialize cart on store creation
  ensureSession()
  
  return {
    // State
    items,
    sessionId,
    loading,
    error,
    
    // Getters
    itemCount,
    subtotal,
    hasItems,
    
    // Actions
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    loadCart,
    ensureSession
  }
})